import React, {useMemo} from 'react';
import {SpecialistPendingCase} from '@/interface';
import {Text, Icon, Button} from '@/ui-kits';
import {formatDateTime, formatName} from '@/utils/common_functions';
import {useNavigate} from 'react-router-dom';
import {Routes} from '@/navigation/Routes';
import { DeadlineTimer } from '@/components/deadline-timer';

interface QuickActionCardProps extends SpecialistPendingCase {}

export const QuickActionCard: React.FC<QuickActionCardProps> = props => {
  const {id, patient_name, deadline_at} = props;
  const navigate = useNavigate();
  const timeRemaining = useMemo(() => {
    const now = new Date();
    const deadline = new Date(deadline_at);
    const diffMs = deadline.getTime() - now.getTime();

    if (diffMs <= 0) return 'Overdue';

    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const timeString = hours > 0 ? `${hours}h${minutes > 0 ? ` ${minutes}m` : ''}` : `${minutes}m`;

    return `${timeString} Remaining`;
  }, [deadline_at]);

  function gotoConsultationSummary(id: number) {
    navigate(Routes.CONSULTATION_SUMMARY.replace(':id', id.toString()));
  }

  return (
    <div className="bg-ui_bg_pale_blue p-4 sm:p-5 border-2 border-gray-300 rounded-lg flex flex-col sm:flex-row gap-3 sm:gap-2 sm:items-center">
      {/* First line: Icon, Name, Case ID */}
      <div className='grid grid-cols-1 gap-2 3xl:grid-cols-2 grow max-w-[700px] 4xl:max-w-none'>
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <Icon type="user2" className="flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <span className="text-ui_input_border_dark font-FigtreeSemiBold text-base break-words">
              {formatName(patient_name)}
            </span>
            <span className="text-ui_input_border_dark text-sm font-medium whitespace-nowrap ml-2">
              ( Case ID: <span className="!font-FigtreeBold">#{id}</span> )
            </span>
          </div>
        </div>

        {/* Second line on mobile, inline on desktop: deadline */}
        <div className="flex flex-col !min-w-full max-w-full sm:flex-row sm:items-center gap-1 sm:gap-2 sm:whitespace-nowrap sm:pr-8 sm:ml-auto">
          <div className="flex items-center gap-2">
            <Icon type="hourglass" className="text-ui_blue flex-shrink-0" />
            <Text variant="body" className="text-ui_blue font-FigtreeSemiBold font-semibold">
              Deadline: {formatDateTime(deadline_at)}
            </Text>
          </div>
          <Text variant="body" className="text-rejection font-FigtreeSemiBold font-semibold sm:ml-0 ml-6">
            <DeadlineTimer deadline_at={deadline_at}/>
          </Text>
        </div>
      </div>
      {/* CTA */}
      <div className="mx-auto sm:mx-0 sm:ml-auto">
        <Button title="Review Case" onClick={() => gotoConsultationSummary(id)} />
      </div>
    </div>
  );
};
